<template>
  <v-dialog
    ref="dialog"
    v-model="isOpen"
    transition="custom-dialog-transition"
  >
    <div class="card-overlay" @click="close"/>
    <v-card style="width: 560px">
      <v-card-title class="flex  align-center">
        <v-icon size="24" class="mr-4">$download</v-icon>
        <h6 class="heading--text d-flex align-center">
          {{ $t('message.importing.modal.title') }}
        </h6>
        <v-btn class="ml-auto" icon x-small @click="close()">
          <v-icon size="24">$close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div class="font-weight-600" style="font-size: 16px;">
          {{ $t('message.importing.modal.desc') }}
        </div>
        <div
          v-if="hasInstallFailed"
          class="alert alert--error-bg alert--box alert--100 my-4 py-3"
        >
          <div class="alert__text-box px-0">
            <div class="d-flex align-center mb-3">
              <v-icon color="error" size="16" class="me-2">$alertTriangle</v-icon>
              <span class="font-weight-700 error--text" style="font-size: 14px;">{{ $t('message.importing.modal.failed') }}</span>
            </div>
            <table v-if="currentStep" class="alert-error-details__table mb-3">
              <tbody>
                <tr>
                  <td class="alert-error-details__label">{{ $t('table.header.step') }}</td>
                  <td class="alert-error-details__value">
                    {{ $t(`heading.instance.modal.importDetails.step.${currentStep.name}.title`) }}
                    <span v-if="stepPercent !== null" class="alert-error-details__badge ms-2">{{ stepPercent }}%</span>
                  </td>
                </tr>
                <tr v-if="stepCurrentItem">
                  <td class="alert-error-details__label">
                    <span v-if="currentStep.name === 'migrateFilesUsingPlugin'">{{ $t('table.header.currentFile') }}</span>
                    <span v-else>{{ $t('table.header.currentTable') }}</span>
                  </td>
                  <td class="alert-error-details__value alert-error-details__value--file">{{ stepCurrentItem }}</td>
                </tr>
              </tbody>
            </table>
            <div class="alert-error-details__divider" />
            <div class="d-flex align-start mt-2">
              <span class="font-weight-600 error--text me-2" style="font-size: 13px; white-space: nowrap;">{{ $t('message.alertManager.error') }}:</span>
              <span class="error--text" style="font-size: 13px;">{{ instance.import_details && instance.import_details.message ? instance.import_details.message : '...' }}</span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="well well--import-progress my-4"
        >
          <div class="well__content">
            <span v-if="currentStep" class="mb-2">
              {{ $t(`heading.instance.modal.importDetails.step.${currentStep.name}.title`) }}
            </span>
            <span v-else class="mb-2">
              {{ $t('message.importing.modal.progress') }}
            </span>
            <span v-if="stepPercent">
              {{ stepPercent }}%
            </span>
          </div>
            <v-progress-linear
              :value="stepPercent === null ? 0 : stepPercent"
              :indeterminate="stepPercent === null"
              color="primary"
              class="import-pending__progress"
              rounded
            />
        </div>
        <v-data-table
          :items="importStepsTableItems"
          :headers="[{ text: $t('table.header.step'), value: 'stepName' }, { text: $t('table.header.status'), value: 'status' }]"
          class="custom-data-table custom--import-data-table data-table"
          hide-default-footer
          disable-pagination
        >
          <template v-slot:item.stepName="{ item }">
            <div class="d-flex align-center text-start">
              <span>{{ $t(`heading.instance.modal.importDetails.step.${item.stepName}.title`) }}</span>
              <v-tooltip
                transition="custom-tooltip"
                open-delay="150"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    class="ms-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    $infoCircle
                  </v-icon>
                </template>
                <span>{{ $t(`heading.instance.modal.importDetails.step.${item.stepName}.tooltip`) }}</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :class="statusChipColor(item.status)"
              label
              class="v-chip-faded v-chip-faded--min-w-100 no-pointer"
            >
              <v-icon color="primary" v-if="item.status == 'completed'" size="16">$check</v-icon>
              <loader color="sand" v-else-if="item.status == 'in-progress'" :noMargins="true" :size="16" />
              <v-icon color="error" v-else-if="item.status == 'failed'" size="16">$alertTriangle</v-icon>
              <v-icon color="sand darken-2" v-else-if="item.status == 'stopped'" size="16">$pause</v-icon>
              <span class="">{{ $t(`message.status.${item.status}`) }}</span>
            </v-chip>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions class="d-flex flex-wrap px-8 pt-8">
        <template v-if="instance && instance.import_details && hasInstallFailed">
          <div class="w-100">
            <v-btn
              elevation="0"
              color="primary"
              large
              block
              @click="retryImport"
            >
              {{ $t('button.try') }}
            </v-btn>
          </div>
          <div class="w-100">
            <v-btn
              class="mt-4"
              elevation="0"
              large
              block
              @click="close"
            >
              {{ $t('button.close') }}
            </v-btn>
          </div>
        </template>
        <template v-else>
          <div class="w-100">
            <v-btn
              elevation="0"
              color="primary"
              large
              block
              @click="close"
            >
              {{ $t('button.close') }}
            </v-btn>
          </div>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Loader from "../Loader.vue";
import Api from "@/apis/Api";

export default {
  name: "ImportInstanceDetailsModal",
  components: {
    Loader,
  },
  data() {
    return {
      isOpen: false,
      instance: null,
      activeInstanceId: null,
      onImportStatusChangedHandler: null,
    }
  },
  computed: {
    hasInstallFailed() {
      const inst = this.instance;
      if (!inst || typeof inst.hasInstallFailed !== 'function') return false;
      try {
        return !!inst.hasInstallFailed();
      } catch (_) {
        return false;
      }
    },
    importSteps() {
      if (!this.instance?.import_details?.steps) {
        return {};
      }
      return this.instance.import_details.steps;
    },
    importStepsTableItems() {
      const steps = this.importSteps;
      return Object.keys(steps).map((key) => ({
        stepName: key,
        status: steps[key],
      }));
    },
    currentStep() {
      return this.instance?.import_details?.current_step ?? null;
    },
    stepPercent() {
      if (!this.currentStep) return null;
      const p = this.currentStep.percent;
      return typeof p === 'number' ? p : null;
    },
    stepCurrentItem() {
      if (!this.currentStep) return null;
      return this.currentStep.current_item ?? null;
    }
  },
  methods: {
    retryImport() {
      if (!this.instance) {
        return;
      }
      this.$root.$emit("instance-retry-install", this.instance);
      this.close();
    },
    close() {
      this.isOpen = false;
    },
    isImportFinishedSuccessfully(instance) {
      const status = instance?.async_status?.installation ?? null;
      if (!status) return false;

      return !['pending', 'in_progress', 'failed'].includes(status);
    },
    show(instance) {
      this.activeInstanceId = instance?.id ?? null;
      this.instance = Api.instances().instanceResource(instance);
      this.isOpen = true;
    },
    statusChipColor(status) {
      const map = {
        'not-started': 'v-chip-faded--gray-lighten',
        'in-progress': 'v-chip-faded--sand-lighten',
        'completed': 'v-chip-faded--primary-lighten',
        'failed': 'v-chip-faded--error-lighten',
        'stopped': 'v-chip-faded--warning-lighten',
      };
      return map[status] || 'grey lighten-3';
    },
  },
  mounted() {
    this.onImportStatusChangedHandler = (instance) => {
      if (!this.isOpen) return;
      const incomingId = instance?.id ?? null;
      if (!incomingId || incomingId !== this.activeInstanceId) return;
      this.instance = Api.instances().instanceResource(instance);
      if (this.isImportFinishedSuccessfully(instance)) {
        this.close();
      }
    };

    this.$root.$on("import-status-changed", this.onImportStatusChangedHandler);
  },
  beforeDestroy() {
    if (this.onImportStatusChangedHandler) {
      this.$root.$off("import-status-changed", this.onImportStatusChangedHandler);
    }
  }
}
</script>

<style scoped lang="scss">
.v-chip::v-deep{
  &.v-chip-faded{
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 6px;
    .v-chip__content{
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
    }
    &--gray-lighten{
      background: #F2F2F7;
      color: var(--v-gray-darken1);
    }
    &--sand-lighten{
      background: rgba(255, 205, 81, 0.15);
      color: var(--v-sand-darken2);
    }
    &--primary-lighten{
      background: rgba(7, 192, 126, 0.10);
      color: var(--v-primary-base);
    }
    &--error-lighten{
      background: rgba(231, 29, 29, 0.15);
      color: var(--v-error-base);
    }
    &--warning-lighten{
      background: rgba(255, 205, 81, 0.15);
      color: var(--v-sand-darken2);
    }
    &--min-w-100{
      min-width: 100px;
      width: min-content;
    }
  }
}
.v-data-table::v-deep .v-data-table__wrapper {
  overflow-x: auto;
  max-height: 320px;
  overflow-y: auto;
}
.v-data-table::v-deep{
  &.custom-data-table{
    &.custom--import-data-table{
      .v-data-table__wrapper{
        padding: 0 24px;
      }
      .v-data-table-header{
        > tr{
          >th{
            height: 20px;
            span{
              font-size: 12px;
              font-style: normal;
              font-weight: 600;
              line-height: 16px;
              color: var(--v-gray-lighten1);
              text-transform: capitalize;
            }
            &:last-child{
              // padding-left: 16px !important;
            }
          }
        }
      }
      tbody{
        > tr{
          > td{
            &:first-child{
              padding-left: 0!important;
            }
            &:last-child{
              padding-right: 0!important;
              text-align: right !important;
              > span{
                margin-left: auto;
              }
            }
          } 
        }
      }
    }
  }
}
.alert-error-details {
  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    line-height: 20px;
  }
  &__label {
    color: var(--v-gray-lighten1);
    font-weight: 400;
    min-width: 110px;
    padding: 2px 12px 2px 0;
    vertical-align: top;
    white-space: nowrap;
  }
  &__value {
    font-weight: 600;
    color: var(--v-text-base);
    padding: 2px 0;
    vertical-align: top;
    word-break: break-all;
    &--file {
      font-family: monospace;
      font-size: 12px;
      font-weight: 400;
    }
  }
  &__badge {
    display: inline-block;
    background: rgba(231, 29, 29, 0.12);
    color: var(--v-error-base);
    border-radius: 4px;
    padding: 0 5px;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
  }
  &__divider {
    border-top: 1px solid rgba(231, 29, 29, 0.15);
    margin: 4px 0;
  }
}
.well{
  &--import-progress{
    background: var(--v-gray-lighten6);
    padding: 16px;
    padding-bottom: 20px;
    border-radius: 10px;
  }
  &__content{
    font-size: 16px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
  }
}

</style>